import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {DropdownItemMultiline} from '../../../../../../../../../src/components/common/dropdowns/dropdownItemMultiline';
import {DeprecatedSearchDropdown} from '../../../../../../../../../src/components/common/dropdowns/search/deprecatedSearchDropdown';
import {useAsyncDropdownSource} from '../../../../../../../../../src/components/common/dropdowns/source/hooks/useAsyncDropdownSource';
import {CrmListModel} from '../../../../../../../../../src/models/api/crmListModel';
import {CrmSyncSearchListsModel} from '../../../../../../../../../src/models/api/crmSearchListsModel';
import {CrmSyncSearchOptions} from '../../../../../../../../../src/store/data/crmSync/crmSyncActions';
import {HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {DropdownItem} from '../../../../../../../../shared/ui/src/components/dropdowns/dropdownItem';
import {PopoverTransferProps} from '../../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {fontWeights} from '../../../../../../../../shared/ui/src/styles/fontStyles';

/*
 * Constants.
 */

const dropdownItemHeight = 33;
const dropdownHeight = 342;
const searchLimit = 100;

/*
 * Props.
 */

interface CrmSyncsListsDropdownProps {
  popoverProps: PopoverTransferProps;
  selectedListId?: string;
  dropdownTitle: ReactNode;
  onSelect: HandlerOf<CrmListModel>;
  searchListsAsync: (
    query: string,
    options: CrmSyncSearchOptions,
  ) => Promise<CrmSyncSearchListsModel | undefined>;
}

interface CrmSyncsListsDropdownPaginationData {
  nextPageToken?: string;
}

/*
 * Styles.
 */

interface StyledDropdownItemProps {
  $isSelected?: boolean;
}

const StyledDropdownItem = styled(DropdownItem)<StyledDropdownItemProps>`
  ${(p) =>
    p.$isSelected &&
    css`
      font-weight: ${fontWeights.bold};
    `}
`;

/*
 * Component.
 */

export const CrmSyncsListsDropdown: FC<CrmSyncsListsDropdownProps> = (props) => {
  const {popoverProps, selectedListId, dropdownTitle, onSelect, searchListsAsync} = props;
  const dropdownSource = useAsyncDropdownSource<CrmListModel, CrmSyncsListsDropdownPaginationData>(
    {
      fetcher: async (query, items, cancelToken, paginationData) => {
        const response = await searchListsAsync(query, {
          nextPageToken: paginationData?.nextPageToken,
          limit: searchLimit,
          cancelToken,
        });
        cancelToken.throwIfRequested();

        return {
          items: [...items, ...(response?.lists ?? [])],
          canLoadMore: Boolean(response?.nextPageToken),
          paginationData: {
            nextPageToken: response?.nextPageToken,
          },
        };
      },
      renderRow: (item: CrmListModel) => (
        <StyledDropdownItem
          onSelect={() => onSelect(item)}
          isChecked={selectedListId === item.id}
          $isSelected={selectedListId === item.id}
        >
          <DropdownItemMultiline title={item.name} />
        </StyledDropdownItem>
      ),
      rowHeightPredicate: () => dropdownItemHeight,
    },
    [],
  );

  return (
    <DeprecatedSearchDropdown
      {...popoverProps}
      title={dropdownTitle}
      source={dropdownSource}
      height={dropdownHeight}
    />
  );
};
